import React, { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from 'react-intl';
import moment from "moment";
import {
    SidePanel,
    SidePanelHeader,
    SidePanelBody,
    SidePanelFooter,
    Button,
    Select,
    TimeFrame,
    useTimeFrameComponent
} from '@gpstrackit/gtc-react-controls';

import {
    Grid
} from "@mui/material";

import {
    reset,
    searchDynamicFormAnswers,
    openAdvancedFilters,
    closeAdvancedFilters
} from '../redux/slice';

const DynamicFormAnswersAdvancedFilters = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();

    const defaultFrame = "last7days"

    const timeFrameOptions = [
        { id: "today", label: messages['date_last_today'] },
        { id: "yesterday", label: messages['date_last_yesterday'] },
        { id: "last7days", label: messages['date_last_7days'] },
        { id: "last30Days", label: messages['date_last_30days'] },
        { id: "custom", label: messages['date_custom'] },
    ];

    const timeFrame = useTimeFrameComponent(timeFrameOptions, defaultFrame);

    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const loadingDrivers = useSelector(state => state.catalogsRedux.loadingDrivers);
    const dynamicForms = useSelector(state => state.dynamicFormAnswersRedux.dynamicForms);
    const loadingDynamicForms = useSelector(state => state.dynamicFormAnswersRedux.loadingDynamicForms);

    const currentFilters = useSelector(state => state.dynamicFormAnswersRedux.filters);
    const open = useSelector(state => state.dynamicFormAnswersRedux.openAdvancedFilters);
    const loading = useSelector(state => state.dynamicFormAnswersRedux.loading);
    const user = useSelector((state) => state.securityRedux.user);

    const [filters, setFilters] = useState({ form: null, driver: null });

    useEffect(() => {
        if (currentFilters?.avoidFirstLoad && currentFilters?.filters) {
            let form = null;
            let formFilter = currentFilters?.filters?.find(f => f?.filterBy === "formId");
            if (formFilter) {
                form = dynamicForms?.find(d => d?.id === formFilter?.filterValue);
            }

            let driver = null;
            let driverFilter = currentFilters?.filters?.find(f => f?.filterBy === "updatedBy");
            if (driverFilter) {
                driver = drivers?.find(d => d?.driverId === driverFilter?.filterValue);
            }

            setFilters({ driver: driver, form: form});
        }
    }, [currentFilters?.avoidFirstLoad, currentFilters?.filters]);

    const clearFilters = useCallback(() => {
        timeFrame.defaultvalue(defaultFrame);
        let newFilters = { form: null, driver: null };
        setFilters(newFilters);

        dispatch(searchDynamicFormAnswers({
            ...currentFilters,
            filters: [], 
            nextToken: null,
            resetSearch: true,
            openSearch: "",
            dateRange: {
                start: moment().subtract(7, 'days').startOf('day').toDate(),
                end: moment.max(moment()).endOf('day').toDate()
            },
            timeFrame: defaultFrame
        }));

        if (props.resetTable) props.resetTable();
    });

    const apply = useCallback(() => {
        const { startDate, endDate } = timeFrame;
        let newFilters = [];
        if (filters?.form) {
            newFilters.push({ filterBy: "formId", filterValue: filters?.form?.id });
        }

        if (filters?.driver) {
            newFilters.push({ filterBy: "updatedBy", filterValue: filters?.driver?.driverId });
        }

        dispatch(searchDynamicFormAnswers({
            ...currentFilters,
            filters: newFilters,
            nextToken: null,
            resetSearch: true,
            dateRange: {
                start: startDate.toISOString(),
                end: endDate.toISOString()
            },
            timeFrame: timeFrame?.option?.id
        }));

        //if (props.resetTable) props.resetTable();
    });

    return (
        <SidePanel
            isOpen={open}
            onToggle={() => {
                if (!open) {
                    dispatch(openAdvancedFilters())
                } else {
                    dispatch(closeAdvancedFilters())
                }
            }}
            style={{ zIndex: 2, width: '400px', transform: 'translateX(400px)' }}
            contentStyle={{ overflowY: "hidden" }}
        >
            <SidePanelHeader>
                <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item>
                        <h2 style={{ margin: 0 }}>
                            {messages['dynamicFormAnswers_advanceFilters']}
                        </h2>
                        <span
                            className='btn-link'
                            onClick={clearFilters}
                        >
                            {messages['dynamicFormAnswers_clearAll']}
                        </span>
                    </Grid>
                </Grid>
            </SidePanelHeader>

            <SidePanelBody
                style={{
                    height: "auto"
                }}
            >
                <div>
                    <div
                        style={{
                            marginBottom: 15
                        }}
                    >
                        <Select
                            value={filters?.form}
                            label={messages['dynamicFormAnswers_form']}
                            options={dynamicForms}
                            onChange={(newForm) => {
                                setFilters({ ...filters, form: newForm })
                            }}
                            isSearchable
                            isClearable
                            showPillsOutside
                            textField="name"
                            valueField="id"
                        />
                    </div>

                    <div
                        style={{
                            marginBottom: 15
                        }}
                    >
                        <Select
                            value={filters?.driver}
                            label={messages['dynamicFormAnswers_user']}
                            options={drivers}
                            isSearchable
                            isClearable
                            onChange={(newDriver) => {
                                setFilters({ ...filters, driver: newDriver })
                            }}
                            textField="name"
                            valueField="driverId"
                        />
                    </div>

                    <div>
                        <TimeFrame
                            {...timeFrame}
                            label={messages['dynamicFormsMain_timeframe']}
                            placeholder={messages['dynamicFormsMain_timeframe']}
                            customRangeDate={90}
                            maxDate={moment()}
                            filterSwitch={false}
                            withTime={true}
                            dateFormat={user?.dateformat || "MM/DD/YYYY hh:mm A"}
                        />
                    </div>
                </div>
            </SidePanelBody>

            <SidePanelFooter>
                <div style={{ display: 'flex', justifyContent: 'end' }}>
                    <Button
                        variant="secondary"
                        style={{ marginRight: 8 }}
                        onClick={() => dispatch(closeAdvancedFilters())}
                        disabled={loading || loadingDrivers || loadingDynamicForms}
                    >
                        {messages["cancel"]}
                    </Button>

                    <Button
                        variant="primary"
                        disabled={loading || loadingDrivers || loadingDynamicForms}
                        onClick={apply}
                    >
                        {messages["apply"]}
                    </Button>
                </div>
            </SidePanelFooter>
        </SidePanel>
    )
};

export default DynamicFormAnswersAdvancedFilters;